<!-- eslint-disable vue/html-closing-bracket-newline -->
<template>
  <div class="pagination">
    <h4 class="page-count">
      Showing
      <strong class="start">{{ startCount }}</strong> -
      <strong class="end">{{
        endCount > totalItems ? totalItems : endCount
      }}</strong>
      of
      <strong class="items">{{ totalItems }}</strong> items
    </h4>
    <ul>
      <li
        class="previous"
        :class="{
          disabled: disablePrev,
        }">
        <a @click="handlePreviousPage">
          <svg
            width="8"
            height="15"
            viewBox="0 0 8 15"
            fill="none"
            class="arrow">
            <path
              d="M0.212404 6.96465L6.74633 0.221429C7.03304 -0.0738097 7.49754 -0.0738097 7.78497 0.221429C8.07168 0.516668 8.07168 0.996213 7.78497 1.29145L1.76931 7.49963L7.78425 13.7078C8.07095 14.003 8.07095 14.4826 7.78425 14.7786C7.49754 15.0738 7.03231 15.0738 6.74561 14.7786L0.21168 8.03535C-0.0706804 7.74317 -0.0706804 7.25609 0.212404 6.96465Z" />
          </svg>
        </a>
      </li>

      <li
        v-for="page in paginationConfiguration.left"
        :key="page"
        :class="{ active: page + 1 == currentPage }">
        <a class="page-link leftConfig" @click="handlePageSelected(page)">{{
          page + 1
        }}</a>
      </li>
      <li v-if="paginationConfiguration.breakView">
        <a
          class="page-link"
          @click="handleBreakClick(paginationConfiguration.breakViewIndex)"
          >...
        </a>
      </li>
      <li
        v-for="page in paginationConfiguration.right"
        :key="page"
        :class="{ active: page + 1 == currentPage }">
        <a class="page-link rightConfig" @click="handlePageSelected(page)">{{
          page + 1
        }}</a>
      </li>
      <li
        class="next"
        :class="{
          disabled: disableNext,
        }">
        <a @click="handleNextPage">
          <svg
            width="8"
            height="15"
            viewBox="0 0 8 15"
            fill="none"
            class="arrow">
            <path
              d="M7.7876 6.96465L1.25367 0.221429C0.966964 -0.0738097 0.502457 -0.0738097 0.215028 0.221429C-0.0716761 0.516668 -0.0716761 0.996213 0.215028 1.29145L6.23069 7.49963L0.215752 13.7078C-0.0709521 14.003 -0.0709521 14.4826 0.215752 14.7786C0.502457 15.0738 0.967688 15.0738 1.25439 14.7786L7.78832 8.03535C8.07068 7.74317 8.07068 7.25609 7.7876 6.96465Z" />
          </svg>
        </a>
      </li>
    </ul>
  </div>
</template>

<script>
import { generatePaginationConfiguration } from "@/utils/helpers";

export default {
  name: "Pagination",
  props: {
    totalItems: { type: Number, required: true },
    currentPage: { type: Number, required: true },
    pageSize: { type: Number, required: true },
    onPageChange: { type: Function, required: true },
    pageCount: { type: Number, required: true },
  },
  data() {
    return {
      paginationConfiguration: generatePaginationConfiguration(
        this.currentPage,
        this.pageCount
      ),
    };
  },
  computed: {
    disablePrev() {
      return this.currentPage - 1 === 0;
    },
    disableNext() {
      return this.currentPage - 1 === this.pageCount - 1;
    },
    combinedResult() {
      return [this.totalItems + this.currentPage];
    },
    startCount() {
      return this.currentPage === 1
        ? this.currentPage
        : this.currentPage * this.pageSize - this.pageSize + 1;
    },
    endCount() {
      return this.currentPage * this.pageSize;
    },
  },
  watch: {
    combinedResult() {
      this.paginationConfiguration = generatePaginationConfiguration(
        this.currentPage,
        this.pageCount
      );
    },
  },
  methods: {
    handlePreviousPage() {
      if (this.currentPage > 1) {
        this.onPageChange(this.currentPage - 1);
      }
    },
    handleNextPage() {
      if (this.currentPage < this.pageCount) {
        this.onPageChange(this.currentPage + 1);
      }
    },
    handlePageSelected(index) {
      let current = index + 1;
      if (this.currentPage === current) return;
      this.onPageChange(current);
    },
    getForwardJump() {
      const forwardJump = this.currentPage + 2;
      return forwardJump >= this.pageCount ? this.pageCount - 1 : forwardJump;
    },
    getBackwardJump() {
      const backwardJump = this.currentPage - 2;
      return backwardJump < 0 ? 0 : backwardJump;
    },
    handleBreakClick(index) {
      this.handlePageSelected(
        this.currentPage < index
          ? this.getForwardJump()
          : this.getBackwardJump()
      );
    },
  },
};
</script>

<style lang="scss" scoped>
// Styles Imports
@import "@/styles/imports";
.pagination {
  .page-count {
    font-size: 14px;
    font-family: "Maersk Text Light";
    color: var(--secondary-text);
    display: inline-block;
    @include media("<=tablet") {
      display: block;
    }
    strong {
      font-family: "Maersk Text";
    }
  }
  ul {
    display: flex;

    li {
      a {
        color: var(--primary-text);
        font-family: "Maersk Text";
        font-size: 16px;
        background-color: var(--layout-border);
        // width: 32px;
        padding: 4px 16px;
        height: 32px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        line-height: 0;
        border-radius: 4px;
        margin-right: 12px;
        @include media("<=tablet") {
          margin-right: 8px;
          font-size: 14px;
        }
        @include media("<=500px") {
          padding: 2px 6px;
          margin-right: 4px;
        }
        &:last {
          margin-right: 0;
        }
        &:hover {
          background-color: var(--pagination-active-bg);
          color: var(--pagination-active);
          .arrow {
            fill: var(--pagination-active);
          }
        }
        .arrow {
          fill: var(--primary-text);
        }
      }
      &.disabled {
        a {
          cursor: not-allowed;
          background-color: var(--layout-border);
          .arrow {
            fill: var(--primary-text-disabled);
          }
        }
      }
      &.active {
        a {
          position: relative;
          background-color: var(--pagination-active-bg);
          color: var(--pagination-active);
          font-family: "Maersk Text Bold";
          &:after {
            background-color: var(--pagination-active);
            content: "";
            height: 4px;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;
          }
        }
      }
    }
  }
}
</style>
